import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useGlobalState, storage } from 'piral-core';
import { useTranslate } from 'piral-translate';
import { useHistory, useLocation } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PlayIconOutline,
  ClockIcon,
  ArrowRightIcon,
  DocumentMagnifyingGlassIcon,
  StarIcon,
  PlayIcon,
} from '../Icons/Icons';

import { SpotlightItem } from './spotlight.types';
import { useSpotlightStore } from './spotlight.store';
import { SpotlightIcon } from './SpotlightIcon';
import { usePageSearch } from './usePageSearch';
import { useTilesStore } from '../../siplet-api/tiles.store';
import { useKeycloak } from '../../keycloak-api/useKeycloak';
import { useQuickNotesStore } from '../QuickNotes/quickNotes.store';
import { useConfirmDialogStore } from '../ConfirmDialog/ConfirmDialog';
import { useCalendarDialogStore } from '../CalendarDialog/calendarDialog.store';
import './spotlight.css';

export const Spotlight: React.FC = () => {
  const {
    isOpen,
    query,
    selectedIndex,
    mode,
    showActionsButton,
    showHistoryButton,
    showPageSearchButton,
    pageSearchResults,
    historyRoutes,
    open,
    close,
    setQuery,
    setSelectedIndex,
    toggleMode,
    addToHistory,
    showAnimationButtons,
  } = useSpotlightStore();

  const { searchPageText, highlightPageResult, markJustSelected } = usePageSearch();
  const [darkMode, setDarkMode] = React.useState(() => document.documentElement.classList.contains('dark'));

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const translate = useTranslate();
  const { isFavorite } = useTilesStore();
  const { open: openQuickNotes } = useQuickNotesStore();
  const { open: openConfirm } = useConfirmDialogStore();
  const { open: openCalendar } = useCalendarDialogStore();

  // Piral global state
  const tileStorage = useGlobalState(s => s.tileStorage);
  const menuStorage = useGlobalState(s => s.menuStorage);
  const registryMenuItems = useGlobalState(s => s.registry.menuItems);
  const location = useLocation();

  // Get current pilet name from location
  const currentPilet = useMemo(() => {
    const firstSegment = '/' + location.pathname.split('/')[1];
    return firstSegment === '/' ? null : firstSegment;
  }, [location.pathname]);

  // Focus mode state
  const [focusMode, setFocusMode] = React.useState(() => document.documentElement.classList.contains('focus-mode'));

  // Night mode state
  const [nightMode, setNightMode] = React.useState(() => document.documentElement.classList.contains('night-mode'));

  // Action handlers
  const logout = useCallback(() => {
    keycloak?.logout({ redirectUri: window.location.origin });
  }, [keycloak]);

  const toggleFocusMode = useCallback(() => {
    const newFocusMode = !document.documentElement.classList.contains('focus-mode');
    document.documentElement.classList.toggle('focus-mode', newFocusMode);
    setFocusMode(newFocusMode);
  }, []);

  const toggleNightMode = useCallback(() => {
    const newNightMode = !document.documentElement.classList.contains('night-mode');
    document.documentElement.classList.toggle('night-mode', newNightMode);
    setNightMode(newNightMode);
  }, []);

  const doClearCache = useCallback(async () => {
    const theme = storage.getItem('theme');
    const favorites = localStorage.getItem('favorites');
    localStorage.clear();
    if (theme) storage.setItem('theme', theme);
    if (favorites) localStorage.setItem('favorites', favorites);
    sessionStorage.clear();
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    window.location.reload();
  }, []);

  const clearCache = useCallback(() => {
    openConfirm({
      title: translate('clear-cache'),
      message: translate('clear-cache-confirm'),
      confirmLabel: translate('clear-cache'),
      onConfirm: doClearCache,
    });
  }, [openConfirm, translate, doClearCache]);

  const showReleaseNotes = useCallback(() => {
    window.dispatchEvent(new CustomEvent('open-release-notes'));
  }, []);

  const manageAccount = useCallback(() => {
    keycloak?.accountManagement();
  }, [keycloak]);

  const toggleDarkMode = useCallback(() => {
    const newTheme = darkMode ? 'light' : 'dark';
    storage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  }, [darkMode]);

  // Sync darkMode state with DOM changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Build searchable items
  const searchableItems = useMemo((): SpotlightItem[] => {
    const specialEntries: SpotlightItem[] = [
      { title: 'Apps', route: '/', description: 'Go to homepage', imgSource: null, icon: 'home' },
      { title: translate('account-verwalten'), route: '#manage-account', description: 'Manage your account', imgSource: null, icon: 'settings', action: manageAccount },
      { title: translate('abmelden'), route: '#logout', description: 'Log out', imgSource: null, icon: 'logout', action: logout },
      { title: translate('clear-cache'), route: '#clear-cache', description: 'Clear cache and reload', imgSource: null, icon: 'cache', action: clearCache },
      { title: translate('theme-wechseln'), route: '#toggle-theme', description: 'Toggle dark/light mode', imgSource: null, icon: 'theme', action: toggleDarkMode },
      { title: translate('release-notes'), route: '#release-notes', description: 'Show release notes', imgSource: null, icon: 'release-notes', action: showReleaseNotes },
      { title: translate('focus-mode'), route: '#focus-mode', description: 'Toggle focus mode', imgSource: null, icon: 'focus', action: toggleFocusMode },
      { title: translate('night-mode'), route: '#night-mode', description: 'Toggle night mode', imgSource: null, icon: 'night-mode', action: toggleNightMode },
      { title: translate('quick-notes'), route: '#quick-notes', description: 'Open quick notes', imgSource: null, icon: 'notes', action: openQuickNotes },
      { title: translate('calendar'), route: '#calendar', description: 'Open calendar', imgSource: null, icon: 'calendar', action: openCalendar },
    ];

    const tiles = tileStorage ? tileStorage.getAllTiles() : [];
    const tileRoutes = new Set(tiles.map(t => t.route));

    // Get menu items for current pilet
    const menuItems: SpotlightItem[] = [];
    if (menuStorage && currentPilet) {
      const allMenus = menuStorage.getAllMenus();
      for (const menu of allMenus) {
        if (!menu.route.startsWith(currentPilet)) continue;

        if (!tileRoutes.has(menu.route)) {
          menuItems.push({
            title: menu.title,
            route: menu.route,
            description: '',
            imgSource: null,
            icon: 'menu',
            menuIcon: menu.icon,
          });
        }
        if (menu.subItems) {
          for (const subItem of menu.subItems) {
            if (!tileRoutes.has(subItem.route)) {
              menuItems.push({
                title: subItem.title,
                route: subItem.route,
                description: '',
                imgSource: null,
                icon: 'menu',
                menuIcon: subItem.icon,
              });
            }
          }
        }
      }
    }

    return [...specialEntries, ...tiles, ...menuItems];
  }, [tileStorage, menuStorage, currentPilet, registryMenuItems, translate, manageAccount, logout, clearCache, showReleaseNotes, toggleDarkMode, toggleFocusMode, toggleNightMode, openQuickNotes, openCalendar]);

  // Action items only
  const actionItems = useMemo(() => searchableItems.filter(item => item.action), [searchableItems]);

  // History items mapped from routes
  const historyItems = useMemo((): SpotlightItem[] => {
    return historyRoutes
      .map(route => searchableItems.find(item => item.route === route))
      .filter((item): item is SpotlightItem => item !== undefined);
  }, [historyRoutes, searchableItems]);

  // Filtered search results
  const filteredResults = useMemo(() => {
    if (!query.trim()) return [];

    const lowerQuery = query.toLowerCase();
    const matches = searchableItems.filter(item => item.title.toLowerCase().includes(lowerQuery));

    return matches.sort((a, b) => {
      const aIsCurrentPiletMenu = currentPilet && a.icon === 'menu' && a.route.startsWith(currentPilet);
      const bIsCurrentPiletMenu = currentPilet && b.icon === 'menu' && b.route.startsWith(currentPilet);
      if (aIsCurrentPiletMenu && !bIsCurrentPiletMenu) return -1;
      if (!aIsCurrentPiletMenu && bIsCurrentPiletMenu) return 1;

      const aIsFavorite = isFavorite(a.route);
      const bIsFavorite = isFavorite(b.route);
      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      return 0;
    });
  }, [query, searchableItems, isFavorite, currentPilet]);

  // Get current items based on mode
  const getCurrentItems = useCallback(() => {
    switch (mode) {
      case 'history': return historyItems;
      case 'actions': return actionItems;
      case 'pageSearch': return [];
      default: return filteredResults;
    }
  }, [mode, historyItems, actionItems, filteredResults]);

  // Navigate to result
  const navigateToResult = useCallback((result: SpotlightItem) => {
    addToHistory(result.route);
    close();
    if (result.action) {
      // Use setTimeout to ensure spotlight is fully closed before action runs
      setTimeout(() => result.action!(), 0);
    } else {
      history.push(result.route);
    }
  }, [history, addToHistory, close]);

  // Handle page search result selection
  const handlePageSearchSelect = useCallback((result: typeof pageSearchResults[0]) => {
    markJustSelected();
    highlightPageResult(result, close);
  }, [highlightPageResult, close, markJustSelected]);

  // Keyboard shortcuts
  useEffect(() => {
    const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
    const handleKeyDown = (e: KeyboardEvent) => {
      const modifierKey = isMac ? e.metaKey : e.ctrlKey;
      if (e.key === 'k' && modifierKey) {
        e.preventDefault();
        // Only open spotlight when logged in
        if (!keycloak?.authenticated) return;
        isOpen ? close() : open();
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, open, close, keycloak?.authenticated]);

  // Focus input and trigger animations when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      showAnimationButtons();
    }
  }, [isOpen, showAnimationButtons]);

  // Re-focus input when mode changes
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [mode, isOpen]);

  // Search page text when in pageSearch mode
  useEffect(() => {
    if (mode === 'pageSearch') {
      searchPageText(query);
    }
  }, [query, mode, searchPageText]);

  // Keyboard navigation in results
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    const currentItems = mode === 'pageSearch' ? pageSearchResults : getCurrentItems();
    const maxIndex = currentItems.length - 1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(selectedIndex >= maxIndex ? 0 : selectedIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(selectedIndex <= 0 ? maxIndex : selectedIndex - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (mode === 'pageSearch' && pageSearchResults[selectedIndex]) {
        handlePageSearchSelect(pageSearchResults[selectedIndex]);
      } else if (currentItems[selectedIndex]) {
        navigateToResult(currentItems[selectedIndex] as SpotlightItem);
      }
    }
  }, [mode, pageSearchResults, selectedIndex, getCurrentItems, setSelectedIndex, handlePageSearchSelect, navigateToResult]);

  // Scroll selected item into view with padding
  useEffect(() => {
    if (resultsRef.current) {
      const container = resultsRef.current;
      const selectedElement = container.querySelector('.neo-spotlight-result-item.selected') as HTMLElement;
      if (!selectedElement) return;

      const padding = 8;
      const containerRect = container.getBoundingClientRect();
      const elementRect = selectedElement.getBoundingClientRect();

      if (elementRect.bottom > containerRect.bottom - padding) {
        container.scrollTop += elementRect.bottom - containerRect.bottom + padding;
      } else if (elementRect.top < containerRect.top + padding) {
        container.scrollTop -= containerRect.top - elementRect.top + padding;
      }
    }
  }, [selectedIndex]);

  // Handle query change
  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, [setQuery]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) close();
  }, [close]);

  if (!isOpen) return null;

  const currentItems = getCurrentItems();
  const hasResults = query || mode !== 'search';

  return (
    <div className="neo-spotlight-backdrop" onClick={handleBackdropClick}>
      <div className="neo-spotlight-wrapper">
        <div className="neo-spotlight-container">
          <div className={`neo-spotlight-input-wrapper ${hasResults ? 'expanded' : ''}`}>
            <MagnifyingGlassIcon className="neo-spotlight-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="neo-spotlight-input"
              placeholder={mode === 'pageSearch' ? translate('search-page') : translate('search-pilets')}
              value={query}
              onChange={handleQueryChange}
              onKeyDown={handleInputKeyDown}
            />
            <span className="neo-spotlight-shortcut">ESC</span>
          </div>

          <div className={`neo-spotlight-results ${!hasResults ? 'collapsed' : ''}`} ref={resultsRef}>
            {/* Page Search Mode */}
            {mode === 'pageSearch' && (
              <>
                <div className="neo-spotlight-section-header">{translate('page-results')}</div>
                {pageSearchResults.length > 0 ? (
                  pageSearchResults.map((result, index) => (
                    <div
                      key={`page-${result.index}`}
                      className={`neo-spotlight-result-item ${index === selectedIndex ? 'selected' : ''}`}
                      onClick={() => handlePageSearchSelect(result)}>
                      <div className="neo-spotlight-result-icon">
                        <DocumentMagnifyingGlassIcon className="neo-spotlight-home-icon" />
                      </div>
                      <div className="neo-spotlight-result-content">
                        <div className="neo-spotlight-result-title neo-spotlight-page-context">{result.context}</div>
                      </div>
                      <ArrowRightIcon className="neo-spotlight-link-icon" />
                    </div>
                  ))
                ) : query ? (
                  <div className="neo-spotlight-no-results">{translate('no-page-results')}</div>
                ) : null}
              </>
            )}

            {/* History Mode */}
            {mode === 'history' && (
              <>
                <div className="neo-spotlight-section-header">{translate('recent-history')}</div>
                {historyItems.length > 0 ? (
                  historyItems.map((result, index) => (
                    <div
                      key={result.route}
                      className={`neo-spotlight-result-item ${index === selectedIndex ? 'selected' : ''}`}
                      onClick={() => navigateToResult(result)}>
                      <div className="neo-spotlight-result-icon">
                        <SpotlightIcon item={result} />
                      </div>
                      <div className="neo-spotlight-result-content">
                        <div className="neo-spotlight-result-title">{result.title}</div>
                      </div>
                      {result.action && <PlayIcon className="neo-spotlight-action-icon" />}
                      {result.icon === 'menu' && <ArrowRightIcon className="neo-spotlight-link-icon" />}
                      {isFavorite(result.route) && <StarIcon className="neo-spotlight-favorite-icon" />}
                    </div>
                  ))
                ) : (
                  <div className="neo-spotlight-no-results">{translate('no-history')}</div>
                )}
              </>
            )}

            {/* Actions Mode */}
            {mode === 'actions' && (
              <>
                <div className="neo-spotlight-section-header">{translate('actions')}</div>
                {actionItems.map((result, index) => (
                  <div
                    key={result.route}
                    className={`neo-spotlight-result-item ${index === selectedIndex ? 'selected' : ''}`}
                    onClick={() => navigateToResult(result)}>
                    <div className="neo-spotlight-result-icon">
                      <SpotlightIcon item={result} />
                    </div>
                    <div className="neo-spotlight-result-content">
                      <div className="neo-spotlight-result-title">{result.title}</div>
                    </div>
                    <PlayIcon className="neo-spotlight-action-icon" />
                  </div>
                ))}
              </>
            )}

            {/* Search Mode */}
            {mode === 'search' && (
              <>
                {filteredResults.length > 0 ? (
                  filteredResults.map((result, index) => (
                    <div
                      key={result.route}
                      className={`neo-spotlight-result-item ${index === selectedIndex ? 'selected' : ''}`}
                      onClick={() => navigateToResult(result)}>
                      <div className="neo-spotlight-result-icon">
                        <SpotlightIcon item={result} />
                      </div>
                      <div className="neo-spotlight-result-content">
                        <div className="neo-spotlight-result-title">{result.title}</div>
                      </div>
                      {result.action && <PlayIcon className="neo-spotlight-action-icon" />}
                      {result.icon === 'menu' && <ArrowRightIcon className="neo-spotlight-link-icon" />}
                      {isFavorite(result.route) && <StarIcon className="neo-spotlight-favorite-icon" />}
                    </div>
                  ))
                ) : query ? (
                  <div className="neo-spotlight-no-results">{translate('no-pilets-found', { query })}</div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <button className={`neo-spotlight-actions ${showActionsButton ? 'morph' : ''}`} onClick={() => toggleMode('actions')} type="button">
          <PlayIconOutline />
        </button>
        <button className={`neo-spotlight-page-search ${showPageSearchButton ? 'morph' : ''}`} onClick={() => toggleMode('pageSearch')} type="button">
          <DocumentMagnifyingGlassIcon />
        </button>
        <button className={`neo-spotlight-history ${showHistoryButton ? 'morph' : ''}`} onClick={() => toggleMode('history')} type="button">
          <ClockIcon />
        </button>
      </div>
    </div>
  );
};
